import { inject, Injectable } from '@angular/core'
import { Paginated } from '@feathersjs/feathers'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { FeathersService } from '../feathers.service'
import { PrimeNgToastService } from '../prime-ng-toast/prime-ng-toast.service'

interface UserSettingRecord {
  id: number
  key: string
  value: unknown
}

interface CacheEntry {
  id: number | undefined
  value: unknown
}

export interface UserSettingOptions<T> {
  defaultValue: T
  isValid: (value: unknown) => value is T
}

/**
 * Generic per-user key-value settings store, backed by the `user-settings`
 * Feathers service. One `key` per feature/dialog; `value` is that feature's own JSON-serializable
 * state. Rows are cached until the next logout or login — nothing here invalidates the cache from
 * another tab or device.
 */
@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private feathers = inject(FeathersService)
  private toaster = inject(PrimeNgToastService)

  private cache = new Map<string, BehaviorSubject<CacheEntry | undefined>>()
  private loads = new Map<string, Promise<void>>()

  constructor() {
    this.feathers.logout$.subscribe(() => {
      this.loads.clear()
      this.cache.forEach((subject) => subject.next(undefined))
    })
    // Re-fetches every key a caller has already asked for, so live subscribers switch over to the
    // rows of whoever just logged in instead of keeping the previous session's values.
    this.feathers.login$.subscribe(() => {
      this.cache.forEach((subject, key) => this.loads.set(key, this.load(key, subject)))
    })
  }

  /**
   * Emits nothing until the stored row has been fetched, then `options.defaultValue` whenever that
   * row is missing or fails `options.isValid` - callers never see raw/invalid data or `undefined`.
   */
  getSetting<T>(key: string, options: UserSettingOptions<T>): Observable<T> {
    const subject = this.getOrCreateSubject(key)
    void this.ensureLoaded(key, subject)
    return subject.asObservable().pipe(
      filter((entry): entry is CacheEntry => entry !== undefined),
      map((entry) => {
        if (options.isValid(entry.value)) return entry.value
        if (entry.value !== undefined) {
          console.warn(`UserSettingsService: ignoring invalid stored value for key "${key}"`, entry.value)
        }
        return options.defaultValue
      }),
    )
  }

  async patchSetting<T>(key: string, value: T): Promise<void> {
    const subject = this.getOrCreateSubject(key)
    // Without this the row's id may still be in flight, and the create below would collide with the
    // row it is about to arrive with.
    await this.ensureLoaded(key, subject)
    const id = subject.value?.id

    try {
      if (id === undefined) {
        const created = (await this.feathers.service('user-settings').create({ key, value })) as UserSettingRecord
        subject.next({ id: created.id, value })
      } else {
        await this.feathers.service('user-settings').patch(id, { value })
        subject.next({ id, value })
      }
      this.toaster.displayCrudToast({ action: 'update', status: 'success' }, 'USER_SETTINGS')
    } catch (error) {
      this.toaster.displayCrudToast({ action: 'update', status: 'error' }, 'USER_SETTINGS')
    }
  }

  private ensureLoaded(key: string, subject: BehaviorSubject<CacheEntry | undefined>): Promise<void> {
    const pending = this.loads.get(key)
    if (pending) return pending

    const load = this.load(key, subject)
    this.loads.set(key, load)
    return load
  }

  private async load(key: string, subject: BehaviorSubject<CacheEntry | undefined>): Promise<void> {
    try {
      const result = (await this.feathers
        .service('user-settings')
        .find({ query: { key } })) as Paginated<UserSettingRecord>
      const row = result.data[0]
      subject.next({ id: row?.id, value: row?.value })
    } catch (error) {
      this.toaster.displayCrudToast({ action: 'read', status: 'error' }, 'USER_SETTINGS')
      subject.next({ id: undefined, value: undefined })
    }
  }

  private getOrCreateSubject(key: string): BehaviorSubject<CacheEntry | undefined> {
    if (!this.cache.has(key)) {
      this.cache.set(key, new BehaviorSubject<CacheEntry | undefined>(undefined))
    }
    return this.cache.get(key) as BehaviorSubject<CacheEntry | undefined>
  }
}
